import { useCallback, useContext, useEffect, useState } from 'react';
import { SessionContext } from '../state/Session';
import UserRoles from '../types/UserRoles';

/**
 * Hook to check if user has access to features in testing.
 * Features in testing are only visible to SuperAdmins and can be toggled with keyboard shortcut.
 *
 * Usage:
 *   const { canSeeTestingFeatures, toggleTestingMode } = useInTesting();
 *   if (canSeeTestingFeatures) { ... }
 *
 * Keyboard shortcut: Press 't' twice quickly (within 500ms) to toggle testing mode.
 */
export const useInTesting = () => {
  const sessionContext = useContext(SessionContext) as any;
  const roles = (sessionContext.session?.user?.metadata ?? []) as string[];
  const isSuperAdmin = roles.indexOf(UserRoles.SuperAdmin) >= 0;

  // Testing mode can be toggled by SuperAdmins via keyboard shortcut
  const [testingModeEnabled, setTestingModeEnabled] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('ospitek_testing_mode') === 'true';
    }
    return false;
  });

  const toggleTestingMode = useCallback(() => {
    if (!isSuperAdmin) return;
    setTestingModeEnabled(prev => {
      const newValue = !prev;
      localStorage.setItem('ospitek_testing_mode', String(newValue));
      return newValue;
    });
  }, [isSuperAdmin]);

  // Keyboard shortcut: t+t (press 't' twice within 500ms)
  useEffect(() => {
    if (!isSuperAdmin) return;

    let lastKeyTime = 0;
    let lastKey = '';

    const handleKeyDown = (e: KeyboardEvent) => {
      // Ignore if typing in an input field
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement ||
        (e.target as HTMLElement).isContentEditable
      ) {
        return;
      }

      const now = Date.now();
      if (e.key === 't' && lastKey === 't' && now - lastKeyTime < 500) {
        toggleTestingMode();
        lastKey = '';
        lastKeyTime = 0;
      } else {
        lastKey = e.key;
        lastKeyTime = now;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isSuperAdmin, toggleTestingMode]);

  return {
    isSuperAdmin,
    testingModeEnabled,
    canSeeTestingFeatures: isSuperAdmin && testingModeEnabled,
    toggleTestingMode,
  };
};

export default useInTesting;
